<template>
  <div class="legend">
    <q-linear-progress v-if="this.isFetching" indeterminate />
    <NotFoundText v-if="!this.data" />
    <div v-else class="content">
      <CustomTable
        title="Реализации по подъезду"
        :rowsData="this.rowsData"
        :columns="this.realizeColumns"
        :loading="isFetching"
        :show-search="true"
        :custom-body="true"
        :sticky-header="false"
      >
        <template #customBody="props">
          <q-tr v-if="!props.row.all">
            <custom-table-cell align="left" vertical-align="center">
              {{ props.row.realize_num_text || props.row.realize_num || '-' }}
            </custom-table-cell>
            <custom-table-cell align="center" vertical-align="center">
              {{ props.row.realize_date || '-' }}
            </custom-table-cell>
            <custom-table-cell align="right" vertical-align="center">
              <Amount :value="props.row.realize_sum" />
            </custom-table-cell>
            <custom-table-cell align="center" vertical-align="center">
              <!-- Статус подписи -->
              <div class="sign-status-icons" style="display: flex; gap: 4px; align-items: center;">
                <template v-if="getSignInfo(props.row)">
                  <div 
                    v-for="(signature, idx) in getSignInfo(props.row).signatures"
                    :key="idx"
                    style="display: flex; align-items: center; gap: 2px;"
                  >
                    <q-icon :name="getSignatureIcon(idx)" size="14px" color="grey-7" />
                    <q-icon 
                      :name="getSignIcon(signature.status).icon"
                      :color="getSignIcon(signature.status).color"
                      size="14px"
                    />
                    <q-tooltip>
                      {{ signature.description || `Подпись ${idx + 1}` }}: {{ getSideSignLabel(signature.status) }}
                    </q-tooltip>
                  </div>
                </template>
                <template v-else>
                  <span style="color: #999; font-size: 12px;">Не подписано</span>
                </template>
              </div>
            </custom-table-cell>
            <custom-table-cell align="center" vertical-align="center">
              <span
                class="status-badge"
                :class="get1CStatusClass(props.row.is_send_to_1c)"
              >
                {{ get1CStatusLabel(props.row.is_send_to_1c) }}
              </span>
            </custom-table-cell>
            <custom-table-cell align="center" vertical-align="center">
              <div style="display: flex; align-items: center; justify-content: center; min-width: 24px; min-height: 24px;">
                <q-spinner
                  v-if="itemsLoading[props.row.realize_id]"
                  color="primary"
                  size="20px"
                />
                <CustomIcon
                  v-else
                  :icon="expandedRows[props.row.realize_id] ? 'expand_less' : 'expand_more'"
                  :primary="true"
                  @click="() => toggleRow(props.row.realize_id)"
                  style="cursor: pointer;"
                />
              </div>
            </custom-table-cell>
            <custom-table-cell align="center" vertical-align="center">
              <CustomDropdown
                :loading="this.realizeActionsLoading[props.row.realize_id]"
                :onClick="(action) => this.handleRealizeAction(props.row, action.action)"
                :options="this.getRealizeActionsDropdown(props.row)"
                title="Действия"
              />
            </custom-table-cell>
          </q-tr>
          
          <!-- Раскрытие с позициями -->
          <q-tr
            v-if="expandedRows[props.row.realize_id]"
            class="expanded-row"
          >
            <custom-table-cell
              :colspan="realizeColumns.length"
              align="left"
              vertical-align="top"
              style="padding: 0; background-color: #f5f5f5;"
            >
              <div class="items-table-wrapper">
                <q-table
                    v-if="getItems(props.row.realize_id).length > 0"
                    :rows="getItems(props.row.realize_id)"
                    :columns="itemsColumns"
                    row-key="realize_tem_id"
                    flat
                    dense
                    hide-pagination
                    :rows-per-page-options="[0]"
                    class="items-table"
                  >
                    <template v-slot:body="itemProps">
                      <q-tr :props="itemProps">
                        <q-td key="material_name" :props="itemProps">
                          <div class="material-name-cell" :title="itemProps.row.material_name">
                            {{ itemProps.row.material_name || '-' }}
                          </div>
                        </q-td>
                        <q-td key="material_cnt" :props="itemProps" class="text-right">
                          {{ itemProps.row.material_cnt || '-' }}
                        </q-td>
                        <q-td key="sell_unit_name" :props="itemProps" class="text-center">
                          {{ itemProps.row.sell_unit_name || '-' }}
                        </q-td>
                        <q-td key="material_price" :props="itemProps" class="text-right">
                          <Amount :value="itemProps.row.material_price" />
                        </q-td>
                        <q-td key="amount" :props="itemProps" class="text-right">
                          <strong><Amount :value="itemProps.row.amount" /></strong>
                        </q-td>
                      </q-tr>
                    </template>
                  </q-table>
                  <div v-else style="padding: 20px; color: #999; text-align: center;">
                    Нет позиций
                  </div>
              </div>
            </custom-table-cell>
          </q-tr>
          
          <!-- Итоговая строка -->
          <q-tr v-if="props.row.all">
            <custom-table-cell colspan="2" align="left" vertical-align="center">
              <b>Итого</b>
            </custom-table-cell>
            <custom-table-cell align="right" vertical-align="center">
              <b><Amount :value="props.row.amount" /></b>
            </custom-table-cell>
            <custom-table-cell colspan="4" align="left" vertical-align="center">
            </custom-table-cell>
          </q-tr>
        </template>
      </CustomTable>
    </div>
  </div>
</template>

<script>
import Amount from "@/components/common/Amount.vue";
import CustomIcon from "@/components/common/CustomIcon.vue";
import CustomLoader from "@/components/common/CustomLoader";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomDropdown from "@/components/common/CustomDropdown";
import NotFoundText from "@/components/common/NotFoundText.vue";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { getRealizes, getRealizeItems, downloadRealize, signRealize, sendRealizeTo1C } from './services';

export default {
  name: "MaterialRealizes",
  components: {CustomTable, CustomTableCell, Amount, CustomIcon, CustomLoader, CustomDropdown, NotFoundText},
  props: ['params'],
  data() {
    return {
      realizeColumns: [
        { name: 'realize_num_text', label: 'Номер', align: 'left' },
        { name: 'realize_date', label: 'Дата', align: 'center' },
        { name: 'realize_sum', label: 'Сумма', align: 'right' },
        { name: 'sign_info', label: 'Подписи', align: 'center' },
        { name: 'is_send_to_1c', label: 'Статус 1С', align: 'center' },
        { name: 'expand', label: 'Позиции', align: 'center' },
        { name: 'actions', label: 'Действия', align: 'center' },
      ],
      itemsColumns: [
        { name: 'material_name', label: 'Материал', field: 'material_name', align: 'left' },
        { name: 'material_cnt', label: 'Кол-во', field: 'material_cnt', align: 'right' },
        { name: 'sell_unit_name', label: 'Ед. изм.', field: 'sell_unit_name', align: 'center' },
        { name: 'material_price', label: 'Цена', field: 'material_price', align: 'right' },
        { name: 'amount', label: 'Итого', field: 'amount', align: 'right' },
      ],
      isFetching: false,
      data: null,
      expandedRows: {},
      itemsCache: {},
      itemsLoading: {},
      downloadLoading: {},
      realizeActionsLoading: {},
    }
  },
  methods: {
    async getData() {
      this.isFetching = true
      const res = await getRealizes(this.params)
      this.isFetching = false
      this.data = res || []
    },
    async reloadDataSilently() {
      // Тихая перезагрузка без loader'а для реактивного обновления после CRUD операций
      try {
        const res = await getRealizes(this.params)
        if (!res) return
        // Просто обновляем данные, Vue автоматически перерисует только изменённые элементы
        this.data = res
      } catch (error) {
        console.error('Ошибка при обновлении данных реализаций:', error)
      }
    },
    async toggleRow(realize_id) {
      const isExpanded = !!this.expandedRows[realize_id]
      
      // Если уже загружаем - игнорируем клик
      if (this.itemsLoading[realize_id]) return
      
      if (isExpanded) {
        this.expandedRows = { ...this.expandedRows, [realize_id]: false }
        return
      }
      
      // Показываем loader вместо кнопки
      this.itemsLoading = { ...this.itemsLoading, [realize_id]: true }
      
      try {
        const items = await getRealizeItems({ realize_id })
        this.itemsCache = { ...this.itemsCache, [realize_id]: items || [] }
        // Раскрываем только после успешной загрузки
        this.expandedRows = { ...this.expandedRows, [realize_id]: true }
      } catch (e) {
        console.error('Ошибка загрузки позиций:', e)
        this.showErrorSnackbar('Ошибка загрузки позиций реализации')
      } finally {
        this.itemsLoading = { ...this.itemsLoading, [realize_id]: false }
      }
    },
    getItems(realize_id) {
      return this.itemsCache[realize_id] || []
    },
    getSignInfo(realize) {
      if (!realize.sign_info) return null
      if (typeof realize.sign_info === 'string') {
        try {
          return JSON.parse(realize.sign_info)
        } catch (e) {
          return null
        }
      }
      return realize.sign_info
    },
    getSignIcon(status) {
      if (status === 'SIGNED') return { icon: 'check_circle', color: 'green' }
      if (status === 'WAITING') return { icon: 'hourglass_empty', color: 'warning' }
      return { icon: 'cancel', color: 'grey-5' }
    },
    getSideSignLabel(status) {
      if (status === 'SIGNED') return 'Подписано'
      if (status === 'WAITING') return 'Ожидание'
      return 'Не подписано'
    },
    getSignatureIcon(index) {
      const icons = ['local_shipping', 'domain', 'business', 'person']
      return icons[index] || 'person'
    },
    get1CStatusLabel(status) {
      if (status === 2) return 'Отправлено в 1С'
      if (status === 1) return 'Готов к отправке'
      return 'Не отправлено'
    },
    get1CStatusClass(status) {
      if (status === 2) return 'sent'
      if (status === 1) return 'ready'
      return 'not-sent'
    },
    getRealizeActionsDropdown(realize) {
      const signInfo = this.getSignInfo(realize)
      const hasSignGroup = !!realize.sign_group_id
      
      // Проверяем статус второй подписи (контрагент/застройщик) - это ваша подпись!
      const contractorSignature = signInfo?.signatures?.[1] // Вторая подпись по order_num
      const contractorSigned = contractorSignature?.status === 'SIGNED'
      
      const isFullySigned = signInfo?.is_fully_signed === true
      
      // Проверяем статус отправки в 1С: 0 - не отправлен, 1 - готов к отправке, 2 - отправлен
      const isSentTo1C = realize.is_send_to_1c === 2
      const canSendTo1C = isFullySigned && !isSentTo1C
      
      return [
        {
          label: contractorSigned ? 'Подписано' : 'Подписать',
          action: 'sign',
          icon: 'verified',
          group: 'actions',
          disabled: contractorSigned,  // Бэкенд сам обработает WAITING - вернёт кэшированный URL
          hint: contractorSigned ? 'Уже подписано' : ''
        },
        {
          label: 'Скачать',
          action: 'download',
          icon: 'download',
          group: 'actions',
          disabled: false,
        },
        {
          label: 'Отправить в 1С',
          action: 'send_to_1c',
          icon: 'send',
          group: 'actions',
          disabled: !canSendTo1C,
          hint: isSentTo1C ? 'Уже отправлено в 1С' : (!isFullySigned ? 'Реализация должна быть полностью подписана' : '')
        },
      ]
    },
    async handleRealizeAction(realize, action) {
      const realizeId = realize.realize_id
      
      if (!realizeId) {
        this.showErrorSnackbar('ID реализации не найден')
        return
      }
      
      this.realizeActionsLoading = { ...this.realizeActionsLoading, [realizeId]: true }
      
      switch (action) {
        case 'sign':
          try {
            const res = await signRealize(realizeId)  // Без signer_type - все автоматически!
            if (res?.sign_url) {
              window.open(res.sign_url, '_blank')
            }
            await this.reloadDataSilently()
          } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.response?.data?.data?.message || error?.message || 'Ошибка при инициации подписания'
            this.showErrorSnackbar(errorMessage)
            await this.reloadDataSilently()
          } finally {
            this.realizeActionsLoading = { ...this.realizeActionsLoading, [realizeId]: false }
          }
          break
        case 'download':
          try {
            const realizeNum = realize.realize_num_text || realize.realize_num || ''
            await downloadRealize(realizeId, realizeNum)
            this.showSucceedSnackbar('Реализация успешно скачана')
          } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.response?.data?.data?.message || error?.message || 'Ошибка при скачивании'
            this.showErrorSnackbar(errorMessage)
          } finally {
            this.realizeActionsLoading = { ...this.realizeActionsLoading, [realizeId]: false }
          }
          break
        case 'send_to_1c':
          try {
            const res = await sendRealizeTo1C(realizeId)
            const message = res?.data?.Message || res?.data?.message || res?.message || 'Реализация успешно отправлена в 1С'
            this.showSucceedSnackbar(message)
            await this.reloadDataSilently()
          } catch (error) {
            // Извлекаем сообщение об ошибке из разных возможных мест
            let errorMessage = 'Ошибка при отправке реализации в 1С'
            
            if (error?.response?.data) {
              const data = error.response.data
              // Проверяем разные варианты структуры ответа
              errorMessage = 
                data.error ||           // exception_handler: { status: false, error: "текст" }
                data.message ||         // custom_response: { message: "текст" }
                data.data?.Message ||   // вложенный объект с заглавной M
                data.data?.message ||   // вложенный объект со строчной m
                data.data?.error ||     // вложенный объект с error
                errorMessage
            } else if (error?.message) {
              errorMessage = error.message
            }
            
            this.showErrorSnackbar(errorMessage)
            await this.reloadDataSilently()
          } finally {
            this.realizeActionsLoading = { ...this.realizeActionsLoading, [realizeId]: false }
          }
          break
      }
    },
    async handleDownloadRealize(realize) {
      const realizeId = realize.realize_id
      
      if (!realizeId) {
        this.showErrorSnackbar('ID реализации не найден')
        return
      }
      
      this.downloadLoading = { ...this.downloadLoading, [realizeId]: true }
      try {
        const realizeNum = realize.realize_num_text || realize.realize_num || ''
        await downloadRealize(realizeId, realizeNum)
        this.showSucceedSnackbar('Реализация успешно скачана')
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Ошибка при скачивании'
        this.showErrorSnackbar(errorMessage)
      } finally {
        this.downloadLoading = { ...this.downloadLoading, [realizeId]: false }
      }
    },
  },
  computed: {
    rowsData() {
      if (!this.data?.length) return []
      const amount = this.data.reduce((sum, cur) => sum + (cur.realize_sum || 0), 0)
      return [...this.data, { all: true, amount }]
    },
  },
  mounted() {
    this.getData()
  },
  setup() {
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar()
    return { showSucceedSnackbar, showErrorSnackbar }
  }
};
</script>

<style scoped lang="scss">
.expanded-row {
  background-color: #f5f5f5;
}

.items-table-wrapper {
  background-color: white;
  margin: 8px;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.items-table {
  background-color: white;
  
  ::v-deep .q-table__top {
    display: none;
  }
  
  ::v-deep .q-table thead tr,
  ::v-deep .q-table tbody td {
    height: 40px;
  }
  
  ::v-deep .q-table thead th {
    font-weight: 600;
    font-size: 13px;
    background-color: #fafafa;
    border-bottom: 2px solid #e0e0e0;
  }
  
  ::v-deep .q-table tbody tr {
    &:hover {
      background-color: #f5f5f5;
    }
  }
  
  ::v-deep .q-table tbody td {
    font-size: 13px;
  }
}

.material-name-cell {
  max-width: 350px;
  white-space: normal;
  line-height: 1.3;
  word-break: break-word;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  
  &.sent {
    background-color: #e8f5e9;
    color: #2e7d32;
  }
  
  &.ready {
    background-color: #fff3e0;
    color: #e65100;
  }
  
  &.not-sent {
    background-color: #f5f5f5;
    color: #757575;
  }
}
</style>
